import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../lib/api';
import { formatCurrency, DEAL_STAGES, PROCUREMENT_SECTORS, TASK_STATUSES } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend
} from 'recharts';
import {
  Download, ChartBar, ChartPie, ChartLine, Table, FileText, FileCsv
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const COLORS = ['#0047FF', '#10B981', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4'];

export default function ReportsPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await reportsApi.getSummary();
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleExport = async (entity, format) => {
    setExporting(true);
    try {
      let response;
      if (entity === 'deals') response = await reportsApi.exportDeals(format);
      else if (entity === 'contacts') response = await reportsApi.exportContacts(format);
      else if (entity === 'companies') response = await reportsApi.exportCompanies(format);
      else if (entity === 'procurement') response = await reportsApi.exportProcurement(format);
      else return;

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entity}_export.${format === 'excel' ? 'xlsx' : format}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${entity} as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Prepare chart data
  const dealStageData = DEAL_STAGES.map(stage => ({
    name: stage.label,
    value: summary?.deal_stats?.[stage.id]?.value || 0,
    count: summary?.deal_stats?.[stage.id]?.count || 0,
    avg: summary?.deal_stats?.[stage.id]?.avg || 0,
  }));

  const sectorData = PROCUREMENT_SECTORS.map(sector => ({
    name: sector.label,
    value: summary?.sector_stats?.[sector.id]?.value || 0,
    count: summary?.sector_stats?.[sector.id]?.count || 0,
  })).filter(d => d.count > 0);

  const taskData = TASK_STATUSES.map(status => ({
    name: status.label,
    count: summary?.task_stats?.[status.id] || 0,
  })).filter(d => d.count > 0);

  const procData = PROCUREMENT_SECTORS.map(sector => ({
    name: sector.label,
    budget: summary?.procurement_stats?.[sector.id]?.budget || 0,
    count: summary?.procurement_stats?.[sector.id]?.count || 0,
  })).filter(d => d.count > 0);

  const leadData = [
    { name: 'High (80+)', value: summary?.lead_distribution?.high || 0, fill: '#10B981' },
    { name: 'Medium (40-79)', value: summary?.lead_distribution?.medium || 0, fill: '#F59E0B' },
    { name: 'Low (<40)', value: summary?.lead_distribution?.low || 0, fill: '#E11D48' },
  ].filter(d => d.value > 0);

  const monthlyData = summary?.monthly_deals || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Reports & Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">Comprehensive business intelligence</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview"><ChartBar size={16} className="mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="deals"><ChartLine size={16} className="mr-1" /> Deals</TabsTrigger>
          <TabsTrigger value="procurement"><ChartPie size={16} className="mr-1" /> Procurement</TabsTrigger>
          <TabsTrigger value="export"><Download size={16} className="mr-1" /> Export</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Deal Pipeline */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Deal Pipeline by Stage</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dealStageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: 12 }} />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} name="Value" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Lead Distribution */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Lead Score Distribution</h3>
              <div className="h-72 flex items-center">
                {leadData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={leadData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {leadData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground w-full text-center">No lead data</p>
                )}
              </div>
            </div>

            {/* Task Status */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Task Distribution</h3>
              <div className="h-72">
                {taskData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={taskData} cx="50%" cy="50%" outerRadius={90} dataKey="count" label={({ name, count }) => `${name}: ${count}`}>
                        {taskData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">No task data</div>
                )}
              </div>
            </div>

            {/* Sector Breakdown */}
            <div className="bg-card border border-border p-6 rounded-sm">
              <h3 className="font-heading text-lg font-semibold mb-4">Deals by Sector</h3>
              <div className="h-72">
                {sectorData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sectorData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={120} />
                      <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: 12 }} />
                      <Bar dataKey="value" fill="#10B981" radius={[0, 2, 2, 0]} name="Value" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">No sector data</div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Deals Tab */}
        <TabsContent value="deals" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-4">Monthly Deal Trends</h3>
            <div className="h-80">
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(value, name) => name === 'value' ? formatCurrency(value) : value} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: 12 }} />
                    <Legend />
                    <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} name="Value" />
                    <Area type="monotone" dataKey="count" stroke="#10B981" fill="#10B981" fillOpacity={0.15} name="Deals Count" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">No monthly data available</div>
              )}
            </div>
          </div>

          {/* Deal Stats Table */}
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Stage</th>
                  <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Count</th>
                  <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Total Value</th>
                  <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Avg Value</th>
                </tr>
              </thead>
              <tbody>
                {dealStageData.map((stage) => (
                  <tr key={stage.name} className="border-b border-border">
                    <td className="p-4 font-medium">{stage.name}</td>
                    <td className="p-4 text-right">{stage.count}</td>
                    <td className="p-4 text-right font-semibold">{formatCurrency(stage.value)}</td>
                    <td className="p-4 text-right text-muted-foreground">{formatCurrency(stage.avg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Procurement Tab */}
        <TabsContent value="procurement" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-4">Procurement Budget by Sector</h3>
            <div className="h-80">
              {procData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={procData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: 12 }} />
                    <Bar dataKey="budget" fill="#F59E0B" radius={[2, 2, 0, 0]} name="Budget" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">No procurement data</div>
              )}
            </div>
          </div>

          {/* Procurement Stats Table */}
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Sector</th>
                  <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Items</th>
                  <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Total Budget</th>
                </tr>
              </thead>
              <tbody>
                {procData.map((item) => (
                  <tr key={item.name} className="border-b border-border">
                    <td className="p-4 font-medium">{item.name}</td>
                    <td className="p-4 text-right">{item.count}</td>
                    <td className="p-4 text-right font-semibold">{formatCurrency(item.budget)}</td>
                  </tr>
                ))}
                {procData.length === 0 && (
                  <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No procurement data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'deals', label: 'Deals', icon: ChartLine },
              { key: 'contacts', label: 'Contacts', icon: Table },
              { key: 'companies', label: 'Companies', icon: Table },
              { key: 'procurement', label: 'Procurement', icon: FileText },
            ].map(({ key, label, icon: Icon }) => (
              <div key={key} className="bg-card border border-border p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{label}</h3>
                    <p className="text-xs text-muted-foreground">Export all {label.toLowerCase()} data</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-sm"
                    disabled={exporting}
                    onClick={() => handleExport(key, 'csv')}
                    data-testid={`export-${key}-csv`}
                  >
                    <FileCsv size={16} className="mr-2" />
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 rounded-sm"
                    disabled={exporting}
                    onClick={() => handleExport(key, 'excel')}
                    data-testid={`export-${key}-excel`}
                  >
                    <FileText size={16} className="mr-2" />
                    Excel
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
